import { useEffect, useState, useCallback } from "react";
import { getConfig, calculate, getEstimate, inr } from "@/lib/api";
import { toast } from "sonner";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { CalculatorForm } from "@/components/CalculatorForm";
import { ResultsDashboard } from "@/components/ResultsDashboard";
import { MaterialBreakdown } from "@/components/MaterialBreakdown";
import { ComparisonView } from "@/components/ComparisonView";
import { EmiEstimator } from "@/components/EmiEstimator";
import { PmayChecker } from "@/components/PmayChecker";
import { Milestones } from "@/components/Milestones";
import { ReportView } from "@/components/ReportView";
import { ShareCard } from "@/components/ShareCard";
import { SeoContent } from "@/components/SeoContent";
import { Footer } from "@/components/Footer";

const DEFAULT_FORM = {
  builtup_area: 1200,
  floors: 2,
  city: "bengaluru",
  quality: "premium",
  foundation: "isolated",
  flooring: "vitrified",
  false_ceiling: false,
  modular_kitchen: false,
  smart_home: false,
  ground_coverage: 65,
  plot_area: null,
};

export default function CalculatorApp() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [result, setResult] = useState(null);
  const [pmayResult, setPmayResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const runCalculate = useCallback(async (payload) => {
    setLoading(true);
    try {
      const data = await calculate(payload);
      setResult(data);
      return data;
    } catch (e) {
      toast.error("Could not calculate estimate. Please try again.");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getConfig()
      .then(setConfig)
      .catch(() => toast.error("Failed to load calculator config"));

    const eid = new URLSearchParams(window.location.search).get("e");
    if (eid) {
      getEstimate(eid)
        .then((doc) => {
          setResult(doc.result);
          if (doc.request) setForm((f) => ({ ...f, ...doc.request }));
          toast.success("Loaded shared estimate");
        })
        .catch(() => {
          toast.error("Shared estimate not found");
          runCalculate(DEFAULT_FORM);
        });
    } else {
      runCalculate(DEFAULT_FORM);
    }
  }, [runCalculate]);

  const handleShare = async () => {
    if (!result) return toast.error("Calculate an estimate first");
    const url = `${window.location.origin}/?e=${result.id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Shareable link copied to clipboard!");
    } catch {
      toast.message("Share this link", { description: url });
    }
  };

  const handleWhatsApp = () => {
    if (!result) return toast.error("Calculate an estimate first");
    const url = `${window.location.origin}/?e=${result.id}`;
    const msg =
      `🏗️ *NirmanDwar* construction estimate\n` +
      `${result.inputs.city} · ${result.inputs.quality}\n` +
      `Built-up: ${inr(result.inputs.total_area)} sq.ft\n` +
      `Total cost: ₹${inr(result.total_cost)}\n\n` +
      `View full breakdown: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const handleSaveImage = async () => {
    if (!result) return toast.error("Calculate an estimate first");
    const node = document.getElementById("share-card");
    if (!node) return;
    try {
      toast.message("Generating image…");
      const html2canvas = (await import("html2canvas")).default;
      const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#0B0F17", useCORS: true });
      const link = document.createElement("a");
      link.download = "nirmandwar-estimate.png";
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Estimate image downloaded!");
    } catch (e) {
      console.error(e);
      toast.error("Could not generate image");
    }
  };

  const handleCalculate = async () => {
    const data = await runCalculate(form);
    if (data) {
      toast.success("Estimate ready!");
      setTimeout(
        () => document.getElementById("results")?.scrollIntoView({ behavior: "smooth" }),
        150
      );
    }
  };

  const handleDownload = () => {
    if (!result) return toast.error("Calculate an estimate first");
    window.print();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Navbar onDownload={handleDownload} canDownload={!!result} />
      <Hero />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 space-y-16">
        {/* Calculator + results split */}
        <section id="calculator" className="scroll-mt-20 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <CalculatorForm
                config={config}
                form={form}
                setForm={setForm}
                onCalculate={handleCalculate}
                loading={loading}
              />
            </div>
          </div>
          <div id="results" className="lg:col-span-7 scroll-mt-20">
            <ResultsDashboard
              result={result}
              pmay={pmayResult}
              onShare={handleShare}
              onWhatsApp={handleWhatsApp}
              onSaveImage={handleSaveImage}
            />
          </div>
        </section>

        {/* Materials */}
        <section id="materials" className="scroll-mt-20">
          <MaterialBreakdown result={result} />
        </section>

        {/* Comparison */}
        <section id="comparison" className="scroll-mt-20">
          <ComparisonView builtupArea={form.builtup_area} floors={form.floors} />
        </section>

        {/* Milestones */}
        <section id="milestones" className="scroll-mt-20">
          <Milestones result={result} />
        </section>

        {/* EMI */}
        <section id="emi" className="scroll-mt-20">
          <EmiEstimator totalCost={result?.total_cost} />
        </section>

        {/* PMAY */}
        <section id="pmay" className="scroll-mt-20">
          <PmayChecker
            totalCost={result?.total_cost}
            totalAreaSqft={result?.inputs?.total_area}
            onResult={setPmayResult}
          />
        </section>
      </main>

      <SeoContent />
      <Footer />
      <ReportView result={result} pmay={pmayResult} />
      <ShareCard result={result} pmay={pmayResult} />
    </div>
  );
}
